import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const KEY = 'leendr_cookie_consent_v1';
const TTL_DAYS = 90;

const DEFAULT = {
  necessary: true,   // always on — auth cookies, session
  statistics: false,
  marketing: false,
  comfort: false,
};

const CookieConsentContext = createContext({
  consent: null, hasDecided: false, showBanner: false,
  acceptAll: () => {}, acceptOnlyNecessary: () => {}, save: () => {}, revoke: () => {},
});

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [hasDecided, setHasDecided] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) { setShowBanner(true); return; }
      const parsed = JSON.parse(raw);
      const ageDays = (Date.now() - (parsed.ts || 0)) / (1000 * 60 * 60 * 24);
      if (ageDays > TTL_DAYS) { setShowBanner(true); return; }
      setConsent({ ...DEFAULT, ...(parsed.consent || {}) });
      setHasDecided(true);
    } catch {
      setShowBanner(true);
    }
  }, []);

  const save = useCallback((c) => {
    const final = { ...DEFAULT, ...c, necessary: true };
    localStorage.setItem(KEY, JSON.stringify({ consent: final, ts: Date.now() }));
    setConsent(final);
    setHasDecided(true);
    setShowBanner(false);
  }, []);

  const acceptAll = useCallback(() => save({ necessary: true, statistics: true, marketing: true, comfort: true }), [save]);
  const acceptOnlyNecessary = useCallback(() => save({ necessary: true, statistics: false, marketing: false, comfort: false }), [save]);
  const revoke = useCallback(() => {
    localStorage.removeItem(KEY);
    setConsent(null);
    setHasDecided(false);
    setShowBanner(true);
  }, []);

  return (
    <CookieConsentContext.Provider value={{ consent, hasDecided, showBanner, acceptAll, acceptOnlyNecessary, save, revoke }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
